/**
 * 快速筛选相关的工具函数和hooks
 * 提供筛选数据转换、计数统计等核心功能
 */


import { FieldType } from './enum.js'
import { getOptions } from './useToggle.js'

/**
 * 首字母大写转换函数
 * @param str 要转换的字符串
 * @returns 首字母大写的字符串
 */
export function capitalizeFirstLetter(str) {
	if (!str)
		return '' // 防止空字符串报错
	return str.charAt(0).toUpperCase() + str.slice(1)
}

/**
 * 获取选项的显示名称
 * @param opt 选项对象
 * @returns 优先返回nickname，否则返回name
 */
export function getName(opt) {
	return opt.nickname || opt.name
}

export function getFilterValue(filter, selectData, filterOptions) {
	const fomf = filterOptions.map(x => x.mappingField)
	const assignData = { ...filter, ...selectData }
	fomf.forEach(x => {
		if (x in assignData) {
			assignData[x] = selectData[x] || null
		}
	})
	return assignData
}

/**
 * 获取选项的筛选值
 * @param opt 选项对象
 * @returns 筛选值对象
 */
export function getDefaultFilterValue(opt) {
	const level = [FieldType.SINGLE, FieldType.LABEL, FieldType.CHECKBOX].includes(opt.fieldType || '') ? (opt?.options?.[0]?.options?.length ? 3 : 2) : -1
	const o = {
		...opt,
		filterMode: {}, // 筛选模式（全都要有/有1个就行）
		selectAll: {}, // 全选状态
		tabActiveId: level === 3 ? opt?.options?.[0]?.id : opt.id, // 当前激活的标签ID
		level, // 层级深度
		value: opt.fieldType === FieldType.SECTION ? { min: '', max: '' } : [], // 选中的值
		cascadeObj: opt.fieldType === FieldType.SINGLE_CASCADE ? {} : undefined // 级联选择对象
	}
	// 联合商品特殊处理
	if (opt.mappingField === 'unionGameIds') {
		o.tabActiveId = opt.id
		o.filterMode = { [opt.id]: 'ONE' }
	}
	return o
}

/**
 * 将筛选选项转换为搜索数据格式
 * @param params 转换参数
 * @param params.filterOptions 筛选选项
 * @param params.gameId 游戏ID
 * @param params.goodsCatalogueId 商品目录ID
 * @returns 转换后的搜索数据
 */
export function getSelectDataByFilterOptions({
	filterOptions,
	gameId,
	goodsCatalogueId
}) {
	const selectData = {
		gameId,
		goodsCatalogueId: goodsCatalogueId || 6,
		keywords: [],
		goodsSearchActions: [],
		unionGameIds: []
	}

	filterOptions.forEach(item => {
		if (item.mappingField === 'hotLabel')
			return
		// 处理单选和级联单选类型
		if ([FieldType.SINGLE, FieldType.LABEL, FieldType.SINGLE_CASCADE].includes(item.fieldType)) {
			const values = item.value
			if (values.length) {
				if (item.mappingField) {
					// 有映射字段的直接赋值
					selectData[item.mappingField] = values[0]?.id
				}
				else {
					let name = ''
					// 无映射字段的添加到关键词
					if (FieldType.SINGLE_CASCADE === item.fieldType) {
						const ck = Math.max(...Object.keys(item.cascadeObj).map(Number))
						name = item.cascadeObj[ck]?.name || ''
					}
					else {
						if (item.level === 2) {
							name = item.options?.find(x => values.some(v => v?.id === x?.id))?.name || ''
						}
						else {
							item.options?.forEach(x => {
								x.options?.forEach(y => {
									if (values.some(v => v?.id === y?.id)) {
										name = y.name
									}
								})
							})
						}
					}
					selectData.keywords.push(name)
				}
			}
		}
		// 处理多选类型
		else if (item.fieldType === FieldType.CHECKBOX) {
			const values = item.value
			if (values.length) {
				if (item.mappingField === 'unionGameIds') {
					selectData.unionGameIds = values.map(v => v.id)
				}
				else if (item.level === 2) {
					// 2级结构的多选
					selectData.goodsSearchActions.push({
						mappingField: item.id,
						searchType: item.filterMode[item.id] || 'ALL',
						searchList: item.options?.filter(option => values.some(v => v?.id === option?.id)).map(x => x.name) || []
					})
				}
				else if (item.level === 3) {
					// 3级结构的多选，按选项分组
					item.options?.forEach(option => {
						const nameList = option.options?.filter(x => values.some(v => v?.id === x?.id)).map(x => x.name) || []
						if (nameList.length) {
							selectData.goodsSearchActions.push({
								mappingField: item.id,
								searchType: item.filterMode[option.id] || 'ALL',
								searchList: nameList
							})
						}
					})
				}
			}
		}
		// 处理区间类型
		else if (item.fieldType === FieldType.SECTION) {
			selectData[`min_${item.mappingField || ''}`] = item.value?.min || ''
			selectData[`max_${item.mappingField || ''}`] = item.value?.max || ''
		}
	})
	return selectData
}


/**
 * 计算当前筛选条件的数量
 * @param filterOptions 筛选选项列表
 * @returns 筛选条件总数
 */
export function getFilterCount(filterOptions) {
    let count = 0
    filterOptions.forEach(item => {
        // 排除热门标签，不计入筛选条件数量
        if (item.mappingField !== 'hotLabel') {
            if (item.fieldType === FieldType.SECTION) {
                // 区间类型：有最小值或最大值就算1个条件
                if (item.value.min || item.value.max) {
                    count += 1
                }
            }
            else {
                // 其他类型：按选中项的数量计算
                const values = item.value
                count += values.length
            }
        }
    })
    return count
}

export function removeFilterItem(item, filterOptions) {
    filterOptions = filterOptions.map(x => {
        if (x.mappingField === item.mappingField || x.mappingField === 'hotLabel') {
            if (x.fieldType === FieldType.CHECKBOX) {
                const values = x.value
                let nId
                if (x.level === 2) {
                    nId = x.options?.find(o => [o.nickname, o.name].includes(item.name))?.id
                }
                else if (x.level === 3) {
                    x.options?.forEach(a => {
                        a.options?.forEach(b => {
                            if ([b.nickname, b.name].includes(item.name)) {
                                nId = b.id
                            }
                        })
                    })
                }
                x.value = values.filter(v => v?.id !== nId)

                // 更新全选状态：检查当前选项卡的所有选项是否都被选中
                const currentCategoryValues = x.value
                x.selectAll[x.tabActiveId] = getOptions(x).options?.every((x) => currentCategoryValues.some(v => v?.id === x?.id)) ?? false
            }
            else {
                const defaultValue = getDefaultFilterValue(x)
                x = {
                    ...x,
                    ...defaultValue
                }
            }
        }
        return x
    })
    return filterOptions
}


export function resetFilter(filterOptions) {
    filterOptions = filterOptions.map(item => getDefaultFilterValue(item))
    return filterOptions
}

/**
 * 快速筛选hooks
 * @param filterOptions 筛选选项的响应式引用
 * @returns 筛选相关的响应式数据和方法
 */
export default function useFast(
	filterOptions
) {

	// 响应式的筛选条件数量
	const filterCount = getFilterCount(filterOptions)

	return {
		filterCount,
        getFilterCount
	}
}
